import { useAppState, useAppDispatch } from '../../Shared/AppContext';

export function useEventState() {
  return useAppState();
}

export function useEventActions() {
  const dispatch = useAppDispatch();

  function initializeState(authenticatedUser, id, startInEditMode, timezones) {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const inviteEmail = urlParams.get('invite_email');
    const inviteCode = urlParams.get('invite_code');

    dispatch((appState) => ({
      ...appState,
      inEditMode: startInEditMode,
      authenticatedUser,
      loading: true,
      inviteEmail,
      inviteCode,
      event: {
        id: id,
        image: null,
        imageFileName: null,
        title: null,
        shortDescription: null,
        startYear: null,
        startMonth: null,
        startDay: null,
        endYear: null,
        endMonth: null,
        endDay: null,
        startHour: null,
        startMinute: null,
        endHour: null,
        endMinute: null,
        timezoneName: 'Eastern Time (US & Canada)',
        venueType: 'in-person',
        location: null,
        breakdownItems: [],
        description: null,
      },
      newEventBreakdownItem: {
        startYear: null,
        startMonth: null,
        startDay: null,
        endYear: null,
        endMonth: null,
        endDay: null,
        startHour: null,
        startMinute: null,
        endHour: null,
        endMinute: null,
        description: null,
        ordinal: 1,
      },
      newEventAttendee: {
        firstName: '',
        lastName: '',
        email: '',
      },
      authenticatedEventAttendee: null,
      feedbackMessage: null,
      feedbackVariant: null,
      timezones,
    }));
  }

  function onResponse(response) {
    dispatch((appState) => ({ ...appState, ...response }));
  }

  function onError(feedback) {
    dispatch((appState) => ({ ...appState, ...feedback }));
  }

  function updateImageAndImageFileName(image, imageFileName) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        image: image,
        imageFileName: imageFileName,
      }
    }));
  }

  function updateTitle(title) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        title: title,
      }
    }));
  }

  function updateShortDescription(shortDescription) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        shortDescription: shortDescription,
      }
    }));
  }

  function updateStartDate(startYear, startMonth, startDay) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        startYear: startYear,
        startMonth: startMonth,
        startDay: startDay,
      }
    }));
  }

  function updateEndDate(endYear, endMonth, endDay) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        endYear: endYear,
        endMonth: endMonth,
        endDay: endDay,
      }
    }));
  }

  function updateStartTime(startHour, startMinute) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        startHour: startHour,
        startMinute: startMinute,
      }
    }));
  }

  function updateEndTime(endHour, endMinute) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        endHour: endHour,
        endMinute: endMinute,
      }
    }));
  }

  function updateTimezone(timezoneName) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        timezoneName: timezoneName,
      }
    }));
  }

  function updateLocation(location) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        location: location,
      }
    }));
  }

  function updateVenueType(venueType) {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        venueType: venueType,
      }
    }));
  }

  function updateNewEventBreakdownItemStartDate(startYear, startMonth, startDay) {
    dispatch((appState) => ({
      ...appState,
      newEventBreakdownItem: {
        ...appState.newEventBreakdownItem,
        startYear: startYear,
        startMonth: startMonth,
        startDay: startDay,
      }
    }));
  }

  function updateNewEventBreakdownItemEndDate(endYear, endMonth, endDay) {
    dispatch((appState) => ({
      ...appState,
      newEventBreakdownItem: {
        ...appState.newEventBreakdownItem,
        endYear: endYear,
        endMonth: endMonth,
        endDay: endDay,
      }
    }));
  }

  function updateNewEventBreakdownItemStartTime(startHour, startMinute) {
    dispatch((appState) => ({
      ...appState,
      newEventBreakdownItem: {
        ...appState.newEventBreakdownItem,
        startHour: startHour,
        startMinute: startMinute,
      }
    }));
  }

  function updateNewEventBreakdownItemEndTime(endHour, endMinute) {
    dispatch((appState) => ({
      ...appState,
      newEventBreakdownItem: {
        ...appState.newEventBreakdownItem,
        endHour: endHour,
        endMinute: endMinute,
      }
    }));
  }

  function updateNewEventBreakdownItemDescription(description) {
    dispatch((appState) => ({
      ...appState,
      newEventBreakdownItem: {
        ...appState.newEventBreakdownItem,
        description: description,
      }
    }));
  }

  function addNewEventBreakdownItem() {
    dispatch((appState) => ({
      ...appState,
      event: {
        ...appState.event,
        breakdownItems: (appState.event?.breakdownItems || []).concat([appState.newEventBreakdownItem]),
      },
      newEventBreakdownItem: {
        startDay: null,
        endDay: null,
        startHour: null,
        startMinute: null,
        endHour: null,
        endMinute: null,
        description: null,
        ordinal: (appState.newEventBreakdownItem?.ordinal || 1) + 1,
      },
    }));
  }

  function updateFirstName(firstName) {
    dispatch((appState) => ({
      ...appState,
      newEventAttendee: {
        ...appState.newEventAttendee,
        firstName: firstName,
      }
    }));
  }

  function updateLastName(lastName) {
    dispatch((appState) => ({
      ...appState,
      newEventAttendee: {
        ...appState.newEventAttendee,
        lastName: lastName,
      }
    }));
  }

  function updateEmail(email) {
    dispatch((appState) => ({
      ...appState,
      newEventAttendee: {
        ...appState.newEventAttendee,
        email: email,
      }
    }));
  }

  function clearFeedback() {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  return {
    initializeState,
    onResponse,
    onError,
    updateImageAndImageFileName,
    updateTitle,
    updateShortDescription,
    updateStartDate,
    updateEndDate,
    updateStartTime,
    updateEndTime,
    updateTimezone,
    updateLocation,
    updateVenueType,
    updateNewEventBreakdownItemStartDate,
    updateNewEventBreakdownItemEndDate,
    updateNewEventBreakdownItemStartTime,
    updateNewEventBreakdownItemEndTime,
    updateNewEventBreakdownItemDescription,
    addNewEventBreakdownItem,
    updateFirstName,
    updateLastName,
    updateEmail,
    clearFeedback,
  };
}
